import type { DialogueTree } from './types';

/**
 * Saudi Prince. Tone: ornate, courteous, low-key threatening, refers to time
 * and geography in metaphors. Money flows toward you when you behave. Things
 * happen to people who don't.
 */
export const SAUDI_PRINCE_TREE: DialogueTree = {
  contactId: 'saudi-prince',
  rootChoices: [
    {
      id: 'small-talk',
      label: 'thank him for the friendship',
      playerSays: "appreciate you. always.",
      effects: [{ type: 'rep', target: 'best_friend', value: 0 }, { type: 'note', value: 'saudi prince acknowledged' }],
      responses: [
        {
          text: "the appreciation is mutual. it is also recorded, which is itself a kind of appreciation.\n\nyou are well? the markets are well? our investments are well?",
          followUps: [
            { id: 'all-well', label: "all well",
              playerSays: "all well. truly.",
              effects: [{ type: 'cash', value: 25_000 }, { type: 'note', value: '+$25K saudi prince wired you a "thinking of you" gift' }],
              responses: [
                { text: "wonderful.\n\na small token has been wired to your usual account. consider it a gesture. consider every gesture a question." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'request-favor',
      label: 'ask for a favor',
      playerSays: "i need a favor",
      responses: [
        {
          text: "favors are not, between us, transactional. they are inevitable. tell me.\n\n(this conversation will be remembered.)",
          followUps: [
            { id: 'favor-cash', label: "wire me \$200K. quietly.",
              playerSays: "wire me \$200K. quietly.",
              effects: [{ type: 'cash', value: 200_000 }, { type: 'fbiHeat', value: 8 }, { type: 'note', value: '+$200K wired by saudi prince. +8 FBI heat — quietly is a relative term.' }],
              responses: [
                { text: "done.\n\n\"quietly\" is a word with a footnote, however. our transfers are quiet relative to a brass band. they are not quiet relative to your treasury department." },
              ],
            },
            { id: 'favor-introduce', label: "introduce me to someone who matters",
              playerSays: "introduce me to someone who matters",
              effects: [{ type: 'tribeRep', target: 'maga', value: 8 }, { type: 'tribeRep', target: 'press', value: -3 }, { type: 'note', value: 'introduced to a "person of substance" — maga +8 / press -3' }],
              responses: [
                { text: "the introduction will arrive in your calendar by tomorrow. they will already know your name. do not be alarmed by this." },
              ],
            },
            { id: 'favor-make-problem', label: "i have a problem. it should not be a problem.",
              playerSays: "i have a problem. it should not be a problem.",
              effects: [{ type: 'fbiHeat', value: 12 }, { type: 'sanity', value: -5 }, { type: 'note', value: 'saudi prince agreed to make the problem smaller. +12 FBI heat. things were said.' }],
              responses: [
                { text: "the problem you describe will become smaller. this does not mean it disappears. it means it becomes smaller. you will, in time, understand the difference.\n\nour balance is, of course, adjusted accordingly." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'distance',
      label: 'try to step back from the relationship',
      playerSays: "i need some distance from this. for a while.",
      effects: [{ type: 'rep', target: 'best_friend', value: 0 }, { type: 'sanity', value: 4 }, { type: 'fbiHeat', value: -2 }, { type: 'note', value: 'asked saudi prince for distance. unclear if granted.' }],
      responses: [
        {
          text: "distance is a kind of geography. geography is a kind of conversation.\n\nwe will speak less. we will not speak none. that is the offer.",
          followUps: [
            { id: 'distance-accept', label: "i\'ll take it",
              playerSays: "i\'ll take it",
              responses: [
                { text: "good.\n\nthe falcon will hunt without you for a season.\n\nthe falcon does not forget you." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'reject',
      label: 'cut him off entirely',
      destructive: true,
      playerSays: "we\'re done. no more.",
      effects: [{ type: 'fbiHeat', value: -10 }, { type: 'cash', value: -50_000 }, { type: 'sanity', value: 8 }, { type: 'note', value: 'cut off saudi prince. -$50K reclaimed funds, -10 FBI heat. probably.' }],
      responses: [
        {
          text: "this is, regrettably, not a relationship that ends by text.\n\nbut: i acknowledge the request. our channel will go quiet. quietly. the wires will reverse. we will both pretend this conversation did not happen. it did happen.\n\nfarewell.",
        },
      ],
    },

    {
      id: 'rage',
      label: 'rage at the prince',
      destructive: true,
      playerSays: "you don\'t own me",
      effects: [{ type: 'fbiHeat', value: 5 }, { type: 'sanity', value: -8 }, { type: 'note', value: 'raged at saudi prince. this was unwise.' }],
      responses: [
        {
          text: "ownership is a western framework.\n\nour relationship is older than ownership. it is older than friendship. it is older than your country. you should consider this in the morning, when you have slept on it.",
        },
      ],
    },

    {
      id: 'invite-yacht',
      label: 'request a yacht weekend',
      playerSays: "the yacht. this weekend. yes?",
      requires: (ctx) => ctx.cash >= 0,
      responses: [
        { text: "the yacht is available. it is also, allegedly, owned by a different cousin. these are, in our family, equivalent statements.\n\nbring no electronics. bring no entourage. bring no questions. these are the rules.\n\nwe will pick you up by helicopter friday at 0700.",
          followUps: [
            { id: 'yacht-yes', label: "i\'m there",
              playerSays: "i\'m there. friday 0700.",
              effects: [
                { type: 'cash', value: -8_000 },
                { type: 'sanity', value: 6 },
                { type: 'fbiHeat', value: 4 },
                { type: 'note', value: '-$8K (no-questions weekend on the yacht). +6 sanity. +4 FBI heat.' },
              ],
              responses: [
                { text: "good. the helicopter will not announce itself. neither will the captain. neither will the menu. enjoy." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'congrats-state-visit',
      label: 'congratulate him on the state visit',
      playerSays: "saw the state visit on the news. impressive.",
      effects: [{ type: 'tribeRep', target: 'maga', value: 3 }, { type: 'note', value: 'flattered the prince. +3 maga (small world).' }],
      responses: [
        { text: "the visit was, by every measure that matters, a success.\n\nthe measures that did not matter — your press, certain european functionaries — produced their predictable noise. the visit was a success regardless." },
      ],
    },
  ],
};
